import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import Analytics from "js/analytics";
import { Button } from "js/Components/Button";
import { getStaticUrl } from "js/config";
import BillingController, { BillingControllerState } from "js/controllers/BillingController";
import { BeautifulDialog, ShowDialogAsync } from "js/react/components/Dialogs/BaseDialog";
import { Gap10 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { themeColors } from "js/react/sharedStyles";
import { formatPrice } from "js/react/views/Billing/utils/prices";
import { Icon } from "js/Components/Icon";

import ChangePaymentMethodDialog from "./ChangePaymentMethodDialog";
import { ContentFrame, ContentFrameTitle, DialogActions, DialogBody, DialogContent, DialogTitle, FeatureText, WorkspaceSelect } from "./components/DialogComponents";

const BackButtonContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    width: 100%;

    > .bai-button {
        color: #666;
        padding: 10px 13px 10px 5px;

        .bai-icon {
            color: #666 !important;
        }

        &:before {
            background: ${themeColors.lightGray} !important;
        }

        &:hover:before {
            background: ${themeColors.lightGray} !important;
        }
    }
`;

function TrialPausedDialog(props: { onClose: () => void, subscriptionLatestInvoicePaymentAttempts: number, lockWorkspace: boolean } & BillingControllerState) {
    const { onClose, subscriptionLatestInvoicePaymentAttempts, lockWorkspace, stripeData: { subscription } } = props;
    const { t } = useTranslation();

    const [showWorkspaceSelect, setShowWorkspaceSelect] = useState(false);

    useEffect(() => {
        Analytics.Dunning.TrialPausedDialogOpened();
    }, []);

    const handleUpdatePaymentMethod = async () => {
        Analytics.Dunning.TrialPausedDialogUpdatePaymentMethodClicked();

        const result = await ShowDialogAsync(ChangePaymentMethodDialog);
        if (result !== false && result?.canceled !== true) {
            Analytics.Dunning.TrialPausedDialogPaymentMethodUpdated();
            onClose();
        }
    };

    const handleContinueInAnotherWorkspace = () => {
        Analytics.Dunning.TrialPausedDialogContinueInAnotherWorkspaceClicked();
        setShowWorkspaceSelect(true);
    };

    const handleBack = () => {
        setShowWorkspaceSelect(false);
    };

    const handleSwitchWorkspace = () => {
        Analytics.Dunning.TrialPausedDialogSwitchWorkspaceClicked();
        onClose();
    };

    const handleCreateWorkspace = () => {
        Analytics.Dunning.TrialPausedDialogCreateWorkspaceClicked();
        onClose();
    };

    const handleNotNow = () => {
        Analytics.Dunning.TrialPausedDialogNotNowClicked();
        onClose();
    };

    if (showWorkspaceSelect) {
        return (
            <BeautifulDialog preventClose closeDialog={onClose}>
                <DialogBody>
                    <DialogTitle>
                        <img src={getStaticUrl("/images/beautifulai-logos/beautifulai-checkout-logo.svg")} />
                        <Gap10 />
                        <div>{t("Navigate to another workspace to continue")}</div>
                    </DialogTitle>
                    <WorkspaceSelect
                        onSwitchWorkspace={handleSwitchWorkspace}
                        onCreateWorkspace={handleCreateWorkspace}
                    />
                    <BackButtonContainer>
                        <Button onClick={handleBack} large unframed>
                            <Icon large>chevron_left</Icon>
                            {t("Back")}
                        </Button>
                    </BackButtonContainer>
                </DialogBody>
            </BeautifulDialog>
        );
    }

    const latestInvoice = subscription.latest_invoice;
    const nextRetry = latestInvoice?.next_payment_attempt;
    const chargeFailureMessage = latestInvoice?.charge?.failure_message ?? "Your card was declined";
    const amount = subscription.current_total_price;
    const currentBillingInterval = subscription.current_billing_interval;

    return (
        <BeautifulDialog preventClose>
            <DialogBody>
                <DialogTitle>
                    <img src={getStaticUrl("/images/beautifulai-logos/beautifulai-checkout-logo.svg")} />
                    <Gap10 />
                    {lockWorkspace && <>
                        <div>{t("The workspace is locked")}</div>
                        <p>
                            {t("We've tried to process your payment {{count}} times without success. Update your payment method to restore access", { count: subscriptionLatestInvoicePaymentAttempts })}
                            &nbsp;
                            <a onClick={handleContinueInAnotherWorkspace}>{t("or continue in another workspace.")}</a>
                        </p>
                    </>}
                    {!lockWorkspace && <>
                        <div>{t("Your trial is on hold")}</div>
                        <p>{t("We couldn't process your payment. Update the payment method to restore access.")}</p>
                    </>}
                </DialogTitle>
                <DialogContent>
                    <ContentFrame dividers>
                        <FlexBox fill horizontalAlign="between" gap={10}>
                            <ContentFrameTitle>{t("reason")}</ContentFrameTitle>
                            <FeatureText dimmed>{chargeFailureMessage}</FeatureText>
                        </FlexBox>
                        <FlexBox fill horizontalAlign="between" gap={10}>
                            <ContentFrameTitle>{t("next retry")}</ContentFrameTitle>
                            <FeatureText dimmed>{t("We'll automatically try again on {{date}}", { date: moment(nextRetry * 1000).format("MMM DD, YYYY") })}</FeatureText>
                        </FlexBox>
                        <FlexBox fill horizontalAlign="between" gap={10}>
                            <ContentFrameTitle>{t("amount")}</ContentFrameTitle>
                            <FeatureText dimmed>{formatPrice(amount, "USD")}/{currentBillingInterval}</FeatureText>
                        </FlexBox>
                    </ContentFrame>
                </DialogContent>
                <DialogActions>
                    {!lockWorkspace &&
                        <Button onClick={handleNotNow} large color={themeColors.lightGray} style={{ color: "#666" }}>
                            {t("Not Now")}
                        </Button>
                    }
                    <Button blue large onClick={handleUpdatePaymentMethod}>
                        {t("Update Payment Method")}
                    </Button>
                </DialogActions>
            </DialogBody>
        </BeautifulDialog>
    );
}

export default BillingController.withInitializedState(TrialPausedDialog);
